import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/layout/Navbar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Wallet, Flag, Settings, Star } from "lucide-react";
import MessageDrawer from "@/components/MessageDrawer";
import LeadCard from "@/components/contractor/LeadCard";
import DisputeModal from "@/components/contractor/DisputeModal";
import BidDraftModal from "@/components/contractor/BidDraftModal";
import { logger } from "@/lib/logger";

export default function ContractorDashboard() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("available");
  const [jobs, setJobs] = useState([]);
  const [unlocked, setUnlocked] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [zip, setZip] = useState("");
  const [boosts, setBoosts] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [chatJob, setChatJob] = useState(null);
  const [sort, setSort] = useState("score");
  const [quality, setQuality] = useState("");
  const [disputeFor, setDisputeFor] = useState(null);
  const [draftFor, setDraftFor] = useState(null);

  const loadJobs = useCallback(async () => {
    const params = {};
    if (cat) params.category_id = cat;
    if (zip) params.zip_code = zip;
    if (sort) params.sort = sort;
    if (quality) params.label = quality;
    try {
      const [j, u, b, d] = await Promise.all([
        api.get("/jobs", { params }),
        api.get("/contractor/unlocked-leads"),
        api.get("/contractor/me/boosts"),
        api.get("/disputes/mine"),
      ]);
      setJobs(j.data); setUnlocked(u.data); setBoosts(b.data); setDisputes(d.data);
    } catch (err) {
      logger.error("[contractor] loadJobs failed", err);
    }
  }, [cat, zip, sort, quality]);

  useEffect(() => {
    api.get("/categories")
      .then((r) => setCats(r.data))
      .catch((err) => logger.error("[contractor] categories load failed", err));
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const unlock = async (job) => {
    try {
      const r = await api.post(`/leads/${job.job_id}/unlock`);
      if (r.data.method === "subscription") {
        toast.success("Lead unlocked via your subscription.");
      } else {
        toast.success(`Lead unlocked. -$${r.data.charged}`);
      }
      await refresh();
      await loadJobs();
    } catch (e) {
      const msg = e.response?.data?.detail || "Unlock failed";
      toast.error(msg);
      if (e.response?.status === 402) {
        setTimeout(() => (window.location.href = "/pricing"), 1500);
      }
    }
  };

  const sub = user?.subscription_tier;
  const subValid = sub && user?.subscription_expires && new Date(user.subscription_expires) > new Date();

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="overline mb-3">// Pro dashboard</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter">
              Hello, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="text-muted-foreground mt-2">Browse fresh leads. Unlock the ones that fit.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/contractor/profile" className="brut-btn" data-testid="contractor-profile-link">
              <Settings className="w-4 h-4 mr-1" /> Profile
            </Link>
            <Link to="/pricing" className="brut-btn-acid" data-testid="contractor-pricing">
              <Wallet className="w-4 h-4 mr-1" /> Buy / Subscribe / Boost
            </Link>
          </div>
        </div>

        {boosts.some((b) => b.active && new Date(b.expires_at) > new Date()) && (
          <div className="brut-card p-4 bg-acid mb-6 flex items-center gap-3" data-testid="boost-banner">
            <Star className="w-5 h-5 fill-ink" />
            <div>
              <div className="overline">// Featured pro active</div>
              <div className="font-mono text-xs mt-1">Your listing is boosted to the top until {new Date(boosts.find((b) => b.active && new Date(b.expires_at) > new Date()).expires_at).toLocaleDateString()}.</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-2 border-ink mb-10">
          <Stat label="Credits" n={`$${(user?.credits || 0).toFixed(2)}`} />
          <Stat label="Subscription" n={subValid ? sub.toUpperCase() : "NONE"} />
          <Stat label="Unlocked" n={unlocked.length} />
          <Stat label="Status" n={user?.contractor_approved ? "APPROVED" : "PENDING"} last />
        </div>

        <div className="flex gap-0 border-2 border-ink mb-8 max-w-2xl">
          <button onClick={() => setTab("available")} className={`flex-1 py-3 font-mono uppercase text-xs tracking-widest font-bold ${tab === "available" ? "bg-ink text-acid" : "bg-white"}`} data-testid="tab-available">
            Available ({jobs.length})
          </button>
          <button onClick={() => setTab("mine")} className={`flex-1 py-3 font-mono uppercase text-xs tracking-widest font-bold border-l-2 border-ink ${tab === "mine" ? "bg-ink text-acid" : "bg-white"}`} data-testid="tab-mine">
            My unlocks ({unlocked.length})
          </button>
          <button onClick={() => setTab("disputes")} className={`flex-1 py-3 font-mono uppercase text-xs tracking-widest font-bold border-l-2 border-ink ${tab === "disputes" ? "bg-ink text-acid" : "bg-white"}`} data-testid="tab-disputes">
            Disputes ({disputes.length})
          </button>
        </div>

        {tab === "available" && (
          <div className="mb-6 space-y-3 max-w-4xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
              <select value={cat} onChange={(e) => setCat(e.target.value)} className="brut-input" data-testid="filter-category">
                <option value="">All categories</option>
                {cats.map((c) => (<option key={c.category_id} value={c.category_id}>{c.name}</option>))}
              </select>
              <input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="Filter by ZIP" className="brut-input" data-testid="filter-zip" />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-0 border-2 border-ink">
                <span className="overline px-3 py-3 bg-bone border-r-2 border-ink">Sort</span>
                {[["score", "Best leads"], ["newest", "Newest"]].map(([v, l]) => (
                  <button key={v} onClick={() => setSort(v)}
                    className={`px-4 py-3 font-mono uppercase text-xs tracking-widest font-bold border-l-2 first:border-l-0 border-ink ${sort === v ? "bg-ink text-acid" : "bg-white"}`}
                    data-testid={`sort-${v}`}>{l}</button>
                ))}
              </div>
              <div className="flex items-center gap-0 border-2 border-ink">
                <span className="overline px-3 py-3 bg-bone border-r-2 border-ink">Quality</span>
                {[["", "All"], ["hot", "Hot"], ["warm", "Warm"]].map(([v, l]) => (
                  <button key={l} onClick={() => setQuality(v)}
                    className={`px-4 py-3 font-mono uppercase text-xs tracking-widest font-bold border-l-2 first:border-l-0 border-ink ${quality === v ? "bg-ink text-acid" : "bg-white"}`}
                    data-testid={`quality-${v || "all"}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab !== "disputes" ? (
          <div className="space-y-4">
            {(tab === "available" ? jobs : unlocked).map((j) => (
              <LeadCard key={j.job_id} job={j} onUnlock={unlock} cats={cats}
                onMessage={() => setChatJob(j)} onDispute={() => setDisputeFor(j)}
                onDraft={() => setDraftFor(j)} />
            ))}
            {(tab === "available" ? jobs : unlocked).length === 0 && (
              <div className="brut-card p-12 text-center bg-white">
                <Sparkles className="w-8 h-8 mx-auto mb-3" />
                <p className="font-mono uppercase text-sm tracking-widest">{tab === "available" ? "No open leads matching your filters" : "You haven't unlocked any leads"}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {disputes.length === 0 && (
              <div className="brut-card p-12 text-center bg-white">
                <Flag className="w-8 h-8 mx-auto mb-3" />
                <p className="font-mono uppercase text-sm tracking-widest">No disputes filed.</p>
              </div>
            )}
            {disputes.map((d) => (
              <div key={d.dispute_id} className="brut-card p-6 bg-white" data-testid={`dispute-${d.dispute_id}`}>
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div>
                    <div className="overline">{d.status.toUpperCase()}</div>
                    <p className="font-display font-bold uppercase text-lg mt-1">{d.reason}</p>
                    <div className="font-mono text-xs mt-2">Charged: ${d.price_charged.toFixed(2)} · {new Date(d.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className={`font-mono text-xs border-2 border-ink px-2 py-1 ${d.status === "refunded" ? "bg-acid" : d.status === "rejected" ? "bg-white" : "bg-bone"}`}>
                    {d.status}{d.refund_amount ? ` · $${d.refund_amount}` : ""}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {chatJob && (
        <MessageDrawer
          jobId={chatJob.job_id}
          otherUser={chatJob.customer_id}
          title={chatJob.title}
          onClose={() => setChatJob(null)}
        />
      )}

      {draftFor && (
        <BidDraftModal job={draftFor} onClose={() => setDraftFor(null)} />
      )}

      {disputeFor && (
        <DisputeModal
          job={disputeFor}
          onClose={() => setDisputeFor(null)}
          onFiled={loadJobs}
        />
      )}
    </div>
  );
}

const Stat = ({ label, n, last }) => (
  <div className={`p-6 bg-white ${!last ? "border-r-2 border-ink" : ""}`}>
    <div className="overline">{label}</div>
    <div className="font-display font-black text-2xl mt-2 truncate">{n}</div>
  </div>
);
